import { api } from "./client";

/**
 * Alerts — alert types and the targets notified for each, backed by the
 * `alert_targets` table via the `/alerts` router.
 *
 * Alert types come from a fixed server-side catalog; a target is a phone
 * number and/or email attached to one type, so a type can have many targets.
 * Writes are admin-only server side.
 */

/** How serious an alert type is — drives the color of its badge. */
export type AlertSeverity = "high" | "moderate";

/** A part of the plant — the sub-tabs on the Alerts configuration page. */
export type AlertArea = {
  key: string;
  label: string;
};

/** Whether an area-wide subscription covers everything or only high severity. */
export type SeverityScope = "all" | "high";

/** An area with the recipients subscribed to the whole of it. */
export type AlertAreaWithTargets = AlertArea & { targets: AlertTarget[] };

/** One entry of the server-side alert type catalog. */
export type AlertType = {
  /** Stable key stored on each target. */
  key: string;
  /** Which area this belongs to, and that area's display name. */
  area: string;
  area_label: string;
  label: string;
  description: string;
  severity: AlertSeverity;
  /** Message text; `{placeholders}` are filled when the alert fires. */
  template: string;
  /** Field names the template expects, in first-appearance order. */
  placeholders: string[];
};

/** One firing of an alert — what the notification center lists. */
export type AlertEvent = {
  id: number;
  alert_type: string;
  /** Catalog labels at read time; type_label falls back to the raw key.
   *  Type labels repeat across areas, so the pair identifies a firing. */
  type_label: string;
  area_label: string;
  severity: AlertSeverity;
  /** The template rendered at fire time. */
  message: string;
  context: Record<string, unknown> | null;
  /** Age computed server-side, so browser clock/timezone can't skew it. */
  age_seconds: number;
  created_at: string;
  read: boolean;
  /** Seeded sample data, not a real firing. */
  is_demo: boolean;
};

/**
 * A recipient. Subscribed to exactly one of: a single alert type, or a whole
 * area. `severity_scope` only bites on area subscriptions — "high" means the
 * high-severity alerts in that area and nothing else.
 */
export type AlertTarget = {
  id: number;
  alert_type: string | null;
  area: string | null;
  severity_scope: SeverityScope;
  /** Who this target is — display only, and optional. */
  name: string | null;
  /** Normalized to E.164 by the server, or null. */
  phone: string | null;
  email: string | null;
  is_active: boolean;
};

/** An alert type with its targets nested — what the Alerts page renders. */
export type AlertTypeWithTargets = AlertType & { targets: AlertTarget[] };

/** Fields the server accepts on target create/update. */
export type AlertTargetInput = {
  alert_type?: string | null;
  area?: string | null;
  severity_scope?: SeverityScope;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  is_active: boolean;
};

/** Every alert type with its targets — one call renders the whole page. */
export function getAlerts(signal?: AbortSignal) {
  return api
    .get<AlertTypeWithTargets[]>("/alerts", { signal })
    .then((r) => r.data);
}

/** Plant areas in tab order, each with its area-wide subscriptions. */
export function getAlertAreas(signal?: AbortSignal) {
  return api
    .get<AlertAreaWithTargets[]>("/alerts/areas", { signal })
    .then((r) => r.data);
}

/** The alert type catalog alone (no targets). */
export function getAlertTypes(signal?: AbortSignal) {
  return api.get<AlertType[]>("/alerts/types", { signal }).then((r) => r.data);
}

/** Flat recipient list, optionally narrowed to one alert type or area. */
export function getAlertTargets(
  opts: { alertType?: string; area?: string } = {},
  signal?: AbortSignal
) {
  return api
    .get<AlertTarget[]>("/alerts/targets", {
      signal,
      params: { alert_type: opts.alertType, area: opts.area },
    })
    .then((r) => r.data);
}

export function createAlertTarget(body: AlertTargetInput) {
  return api.post<AlertTarget>("/alerts/targets", body).then((r) => r.data);
}

export function updateAlertTarget(id: number, body: AlertTargetInput) {
  return api
    .patch<AlertTarget>(`/alerts/targets/${id}`, body)
    .then((r) => r.data);
}

export function deleteAlertTarget(id: number) {
  return api.delete(`/alerts/targets/${id}`).then(() => undefined);
}

/** Recent firings, newest first. */
export function getAlertEvents(
  opts: { limit?: number; unreadOnly?: boolean } = {},
  signal?: AbortSignal
) {
  return api
    .get<AlertEvent[]>("/alerts/events", {
      signal,
      params: { limit: opts.limit, unread_only: opts.unreadOnly },
    })
    .then((r) => r.data);
}

/**
 * Record a firing. The message is rendered server-side from the type's
 * template, so callers pass only the values to fill it with.
 */
export function createAlertEvent(
  alertType: string,
  context?: Record<string, unknown>
) {
  return api
    .post<AlertEvent>("/alerts/events", { alert_type: alertType, context })
    .then((r) => r.data);
}

export function markAlertEventRead(id: number) {
  return api.post<AlertEvent>(`/alerts/events/${id}/read`).then((r) => r.data);
}

/** Send an archived alert back to the current list. */
export function markAlertEventUnread(id: number) {
  return api.post<AlertEvent>(`/alerts/events/${id}/unread`).then((r) => r.data);
}

/** Read state is shared across users, not per-account. */
export function markAllAlertEventsRead() {
  return api.post("/alerts/events/read-all").then(() => undefined);
}

/** Deletes history for everyone — admin only, server side. */
export function clearAlertEvents() {
  return api.delete("/alerts/events").then(() => undefined);
}
